import React, { useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import ProtectedRoute from "./components/ProtectedRoute";
import { AuthProvider } from "./context/AuthContext";
import { initializeSession, cleanupSession } from "./services/sessionService";
import ErrorBoundary from "./components/ErrorBoundary";
import { isDevelopment } from "./utils/development";

// Pages
import Dashboard from "./pages/Dashboard";
import Users from "./pages/Users";
import Currencies from "./pages/Currencies";
import ExchangeRate from "./pages/ExchangeRate";
import FXSpreadManagement from "./pages/FXSpread";
import Profile from "./pages/Profile";
import SignUp from "./pages/SignUp";
import SignIn from "./pages/SignIn";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import PendingRegistrations from "./pages/cards/PendingRegistrations";
import PhysicalCards from "./pages/cards/PhysicalCards";
import VirtualCards from "./pages/cards/VirtualCards";
import Dispatch from "./pages/cards/Dispatch";
import Cards from "./pages/Cards";
import PendingApproval from "./components/PendingApproval";
import AuditLogs from "./pages/AuditLogs";
import BankAccounts from "./pages/BankAccounts";
import OfflineCollections from "./pages/OfflineCollections";

// Styles
import "./assets/styles/verify-registration.css";

// Components
import Main from "./components/layout/Main";

// Styles
import "antd/dist/reset.css";
import "./assets/styles/main.css";
import "./assets/styles/responsive.css";

function App() {
  useEffect(() => {
    // Initialize session management on app load
    const authToken = sessionStorage.getItem("auth_token");
    const userProfile = sessionStorage.getItem("user_profile");

    if (authToken && userProfile) {
      // User has a valid session, initialize session management
      console.log("Initializing session for authenticated user");
      initializeSession();

      // Add event listener for beforeunload to prevent session clearing
      const handleBeforeUnload = (e) => {
        // Don't clear session data on page refresh
        // Just update the activity timestamp to prevent timeout during reload
        sessionStorage.setItem("last_activity", Date.now().toString());
      };

      // Add event listener for pagehide to handle navigation better
      const handlePageHide = (e) => {
        // Only cleanup if it's a real navigation, not a reload
        if (e.persisted) {
          // Page is being cached (back/forward navigation)
          return;
        }
        // Update activity timestamp
        sessionStorage.setItem("last_activity", Date.now().toString());
      };

      window.addEventListener("beforeunload", handleBeforeUnload);
      window.addEventListener("pagehide", handlePageHide);

      return () => {
        window.removeEventListener("beforeunload", handleBeforeUnload);
        window.removeEventListener("pagehide", handlePageHide);
        // Only cleanup on real unmount, not during HMR
        if (!isDevelopment) {
          cleanupSession();
        }
      };
    }
  }, []);

  return (
    <ErrorBoundary>
      <AuthProvider>
        <div className="App">
          <Routes>
            {/* Public routes */}
            <Route path="/sign-up/:inviteToken" element={<SignUp />} />
            <Route path="/sign-in" element={<SignIn />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/forgotten-password/:token" element={<ResetPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />

            {/* Protected routes */}
            <Route path="/" element={<Main />}>
              <Route path="pending-approval" element={<PendingApproval />} />
              <Route
                path="dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route path="cards">
                <Route
                  index
                  element={
                    <ProtectedRoute>
                      <Cards />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="pending"
                  element={
                    <ProtectedRoute>
                      <PendingRegistrations />
                    </ProtectedRoute>
                  }
              />
              <Route
                path="physical"
                element={
                  <ProtectedRoute>
                    <PhysicalCards />
                  </ProtectedRoute>
                }
              />
              <Route
                path="virtual"
                element={
                  <ProtectedRoute>
                    <VirtualCards />
                  </ProtectedRoute>
                }
              />
              <Route
                path="pending-dispatch"
                element={
                  <ProtectedRoute>
                    <Dispatch />
                  </ProtectedRoute>
                }
              />
            </Route>
            <Route
              path="users"
              element={
                <ProtectedRoute>
                  <Users />
                </ProtectedRoute>
              }
            />
            <Route
              path="currencies"
              element={
                <ProtectedRoute>
                  <Currencies />
                </ProtectedRoute>
              }
            />
            <Route
              path="exchange-rate"
              element={
                <ProtectedRoute>
                  <ExchangeRate />
                </ProtectedRoute>
              }
            />
            <Route
              path="fx-spread"
              element={
                <ProtectedRoute>
                  <FXSpreadManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="profile"
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              }
            />
            <Route
              path="audit-logs"
              element={
                <ProtectedRoute>
                  <AuditLogs />
                </ProtectedRoute>
              }
            />
            <Route
              path="bank-accounts"
              element={
                <ProtectedRoute>
                  <BankAccounts />
                </ProtectedRoute>
              }
            />
            <Route
              path="offline-collections"
              element={
                <ProtectedRoute>
                  <OfflineCollections />
                </ProtectedRoute>
              }
            />
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="*" element={<Navigate to="dashboard" replace />} />
          </Route>
        </Routes>
      </div>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
